import Loading from '../Loading';
import { useUrlParams } from '../../utils/url';
import DirectoryHeader from './DirectoryHeader';
import PractitionerSearch from './PractitionerSearch';
import StepIndicator from '../StepIndicator';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { useDirectory } from 'src/contexts/DirectoryContext';
import Button from '../Button';
import { Organization, PractitionerDirectory, Slot } from 'src/shared/types';
import Slots from './Slots';
import SlotConfirm from './SlotConfirm';
import { useState } from 'react';

export const DirectoryPage = () => {
  const [ hasSearched, setHasSearched ] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <DirectoryHeader hasSearched={hasSearched} />
      <DirectoryBody inModal={false} setHasSearched={setHasSearched} />
    </div>
  );
};

interface ErrorProps {
  error: string;
  setError: (error: string | null) => void;
  organization: Organization | null;
}

const Error = ({ error, setError, organization }: ErrorProps) => {

  return (
    <div className="flex flex-col items-center pt-4 h-screen bg-gradient-to-br from-red-50 to-red-100 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="flex justify-center mb-6">
          <div className="bg-red-100 rounded-full p-4">
            <ExclamationCircleIcon className="w-12 h-12 text-red-600" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h2>
        <p className="text-red-600 text-lg mb-6 leading-relaxed">{error}</p>

        <div className="flex gap-3 justify-center">
          <Button
            onClick={() => {
              setError(null);
              window.location.href = `/${organization?.id}/practitioner/search`;
            }}
            text="Back to Search"
          />
        </div>
      </div>
    </div>
  );
};

interface DirectoryBodyProps {
  inModal: boolean;
  onSchedule?: () => void;
  setHasSearched?: (hasSearched: boolean) => void;
  initialZipCode?: string;
}

export const DirectoryBody = ({ inModal, onSchedule = () => {}, setHasSearched = () => {}, initialZipCode }: DirectoryBodyProps) => {
  const { organization, specialties, googleMapsKey, error, setError, setPractitioner, practitioner, loading } = useDirectory();
  const params = useUrlParams();
  const patientId = params.id;
  const [ showSlots, setShowSlots ] = useState(false);
  const [ step, setStep ] = useState<number>(1);
  const [ selectedSlot, setSelectedSlot ] = useState<Slot | null>(null);

  const handlePractitionerSelect = (practitioner: PractitionerDirectory) => {
    if (!organization) return;

    setPractitioner(practitioner);
    if (!inModal) {
      window.location.href = `/${organization.id}/practitioner/search/${practitioner.practitioner_role_id}`;
    } else {
      setShowSlots(true);
      setStep(2);
    }
  }

  return (
    <>
      {error ? (
        <Error error={error} setError={setError} organization={organization} />
      ) : loading || !organization ? (
        <Loading message="Loading Directory..." />
      ) : (
        <>
          <div className="mx-auto px-3 sm:px-4 pt-4 pb-2">
            <StepIndicator
              step={step}
              stepNames={
                inModal ? ['Search Providers', 'Select Time', 'Confirm Booking'] :
                ['Search Providers', 'Select Time', 'Patient Information', 'Confirm Booking']}
              color="brand"
            />
          </div>

          { inModal && showSlots && practitioner ? (
            step === 3 && selectedSlot && patientId ? (
              <SlotConfirm
                patientId={patientId}
                organization={organization}
                selectedSlot={selectedSlot}
                selectedPractitioner={practitioner}
                onSchedule={() => onSchedule()}
              />
            ) : (
              <Slots
                organization={organization}
                selectedPractitioner={practitioner}
                onSlotSelect={(slot) => {
                  setSelectedSlot(slot);
                  setStep(3);
                }}
              />
            )
          ) : (
            <PractitionerSearch
              mapEnabled={!inModal}
              setHasSearched={setHasSearched}
              organization={organization}
              specialties={specialties}
              onPractitionerSelect={handlePractitionerSelect}
              googleMapsApiKey={googleMapsKey}
              initialZipCode={initialZipCode}
            />
          )}
        </>
      )}
    </>
  );
};
